/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LinkButton, SideNavigation, toast } from '@pmi/dsm-react';
import { CloseThinIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { NavItem } from '@pmi/dsm-react/dist/components/side-navigation/types';
import LogoMobile from '../../../logo-mobile.svg?react';
import './sidenav.scss';
import { resetVisibility, sidenavSelector } from './apiSlice';
import styles from './nav.module.scss';
import { reportLinkClick } from '../../analytics';
import HelpGuides from './HelpGuides';
import useLogout from '../../hooks/useLogout';
import { getMyPMISiteUrl } from '../../env-vars';
import { useGetProfileImageUrlQuery } from '../Header/apiSlice';
import useUser from '../../hooks/useUser';
import { ProfileImage, ProfileName } from '../ProfileName';
import { getCurrentNavItem } from './SideNav';

export interface MobileNavProps {
  items: NavItem[];
  title: string;
}

export default function MobileNav({ items = [], title }: MobileNavProps) {
  const user = useUser();
  const logout = useLogout();
  const [selectedNavId, setSelectedNavId] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { visible } = useSelector(sidenavSelector);
  const location = useLocation();
  const linkModule = location.pathname.startsWith('/hub/')
    ? 'b2bhub_sidenav'
    : 'b2badmin_sidenav';

  const { data: profileImageUrl } = useGetProfileImageUrlQuery(null, {
    skip: !user,
  });

  useEffect(
    () => setSelectedNavId(getCurrentNavItem(items, location.pathname)),
    [items, location],
  );
  useEffect(
    () => setSelectedNavId(getCurrentNavItem(items, location.pathname)),
    [],
  );

  const pmiSiteUrl = getMyPMISiteUrl();
  if (!visible) return null;
  if (!items.find((i) => i.id === 'logout')) {
    items.push({
      title: 'Logout',
      id: 'logout',
    });
  }
  const handleLogoutClick = async () => {
    await logout();
  };
  const onClick = (event: unknown, nav: NavItem) => {
    const item = { ...nav } as NavItem & {
      openExternally: boolean;
      link: { href: string };
    };
    (event as Event)?.preventDefault();
    if (item.id !== 'logout') {
      reportLinkClick(item.title, item?.link?.href as string, linkModule);
      setSelectedNavId(item.id);
      dispatch(resetVisibility());
      if (item?.link?.href?.startsWith('http')) {
        if (item?.openExternally) {
          const opener = window.open(item?.link?.href, '_blank');
          if (!opener) {
            toast('Popup blocked by browser', 'danger');
          }
        } else {
          window.location.replace(item?.link?.href);
        }
      } else {
        navigate(item?.link?.href, { replace: true });
      }
      navigate(item.link.href);
    } else {
      handleLogoutClick().then();
    }
  };
  return (
    <div
      className={`position-fixed ${styles.mobileNav}`}
      data-adoberegion={linkModule}
      style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <div className="d-flex flex-row position-relative justify-content-center px-1 py-2 mobile-nav-container">
        <div className="d-block">
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <a
            href={pmiSiteUrl}
            className={`d-block ${styles.smallLogo}`}
            onClick={() => reportLinkClick('PMI Logo', pmiSiteUrl, linkModule)}
          >
            <LogoMobile className="w-100 h-100" />
          </a>
        </div>
        <span style={{ marginTop: '-4px' }}>
          <LinkButton
            className="end-0 position-absolute text-dark fw-bold"
            titleText=""
            size="sm"
            icon={CloseThinIcon}
            onClick={() => dispatch(resetVisibility())}
          />
        </span>
      </div>
      <div className="flex-grow-1">
        <div>
          <div className="d-flex align-items-center py-3 profile-block">
            <ProfileImage url={profileImageUrl?.url ?? ''} />
            <ProfileName user={user} navigateToMyPmi />
          </div>
        </div>
        <SideNavigation
          className="mobileNav"
          version="v2"
          navigation={items.map((i) => ({
            ...i,
            ...{ icon: undefined, children: i.children ?? [] },
          }))}
          title={title}
          onClick={onClick}
          selectedNavItemId={selectedNavId}
        />
      </div>
      {items.length > 0 && (
        <div className="position-absolute bottom-0 w-100">
          <HelpGuides isAdminNav={linkModule === 'b2badmin_sidenav'} />
        </div>
      )}
    </div>
  );
}
